import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import type { FilasDesempenho } from "../../../../types/desempenho";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface FilasTabProps {
  data: FilasDesempenho | null;
}

const formatDateBR = (dateStr: string) => {
  if (!dateStr) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{Number(entry.value).toLocaleString('pt-BR')}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomTempoTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    const formatMin = (min: number) => {
      const h = Math.floor(min / 60);
      const m = Math.round(min % 60);
      return `${h}h ${m}m`;
    };
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{formatMin(Number(entry.value))}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function FilasTab({ data }: FilasTabProps) {
  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum dado disponível. Use os filtros acima para buscar informações.
        </CardContent>
      </Card>
    );
  }

  const formatMinutos = (minutos: number) => {
    const horas = Math.floor(minutos / 60);
    const mins = Math.round(minutos % 60);
    return `${horas}h ${mins}m`;
  };

  // Preparar dados de evolução diária (ações)
  const evolucaoDiariaData = data.volumetria?.porDia?.map(dia => ({
    data: formatDateBR(dia.referencia),
    "Treinamentos Enviados": dia.acoes.treinamentosEnviados,
    "Ausências": dia.acoes.ausencias
  })) || [];

  // Preparar dados de tempo por responsável por dia
  const tempoResponsaveisPorDia = React.useMemo(() => {
    const diasMap = new Map<string, Map<string, number>>();

    data.responsaveis.forEach(responsavel => {
      responsavel.periodos.forEach(periodo => {
        const dataInicio = new Date(periodo.dataInicio);
        const dataStr = formatDateBR(dataInicio.toISOString().split('T')[0]);

        if (!diasMap.has(dataStr)) {
          diasMap.set(dataStr, new Map());
        }

        const diaMap = diasMap.get(dataStr)!;
        const tempoAtual = diaMap.get(responsavel.militar.nome) || 0;
        diaMap.set(responsavel.militar.nome, tempoAtual + periodo.duracaoMinutos);
      });
    });

    // Converter para array de objetos para o gráfico
    const result: any[] = [];
    const sortedDias = Array.from(diasMap.keys()).sort((a, b) => {
      const [dA, mA, yA] = a.split('/').map(Number);
      const [dB, mB, yB] = b.split('/').map(Number);
      return (yA - yB) || (mA - mB) || (dA - dB);
    });

    sortedDias.forEach(dia => {
      const obj: any = { data: dia };
      const responsaveisMap = diasMap.get(dia)!;
      responsaveisMap.forEach((tempo, nome) => {
        obj[nome] = Math.round(tempo);
      });
      result.push(obj);
    });

    return result;
  }, [data.responsaveis]);

  // Obter lista de responsáveis únicos para o gráfico
  const responsaveisUnicos = React.useMemo(() => {
    return Array.from(new Set(data.responsaveis.map(r => r.militar.nome)));
  }, [data.responsaveis]);

  // Cores para os responsáveis
  const cores = [
    "oklch(0.45 0.15 142)",
    "oklch(0.5 0.2 240)",
    "oklch(0.5 0.2 20)",
    "oklch(0.5 0.2 60)",
    "oklch(0.5 0.2 300)",
    "oklch(0.5 0.2 180)",
  ];

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Responsáveis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalResponsaveis}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tempo Médio</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutos(data.resumo.tempoMedioPorResponsavel)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Ações</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalAcoes}</div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos de Evolução */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Evolução Diária</CardTitle>
            <CardDescription>Ações por dia</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolucaoDiariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Treinamentos Enviados" stroke="oklch(0.5 0.2 240)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Ausências" stroke="oklch(0.5 0.2 20)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tempo por Responsável</CardTitle>
            <CardDescription>Tempo diário de cada responsável (minutos)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={tempoResponsaveisPorDia} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTempoTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {responsaveisUnicos.map((nome, index) => (
                  <Bar
                    key={nome}
                    dataKey={nome}
                    fill={cores[index % cores.length]}
                    stackId="a"
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Responsáveis */}
      <Card>
        <CardHeader>
          <CardTitle>Responsáveis de Fila</CardTitle>
          <CardDescription>Desempenho dos responsáveis por fila de treinamento</CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left pb-2 w-8">#</th>
                <th className="text-left pb-2">Militar</th>
                <th className="text-left pb-2">Patente</th>
                <th className="text-right pb-2">Tempo</th>
                <th className="text-right pb-2">Treinamentos</th>
                <th className="text-right pb-2">Ausências</th>
                <th className="text-right pb-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {[...data.responsaveis]
                .sort((a, b) => b.tempoTotal - a.tempoTotal)
                .map((responsavel, index) => (
                <tr key={responsavel.militar.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/20 transition-colors">
                  <td className="py-2 text-muted-foreground text-xs">{index + 1}</td>
                  <td className="py-2 font-medium">{responsavel.militar.nome}</td>
                  <td className="py-2 text-muted-foreground">{responsavel.militar.patente}</td>
                  <td className="py-2 text-right font-bold">{formatMinutos(responsavel.tempoTotal)}</td>
                  <td className="py-2 text-right">{responsavel.acoes.treinamentosEnviados}</td>
                  <td className="py-2 text-right">{responsavel.acoes.ausencias}</td>
                  <td className="py-2 text-right">{responsavel.acoes.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
